import React, { useState, useEffect } from 'react';
import { 
  Star, 
  Users, 
  Gift, 
  Copy, 
  Share2, 
  TrendingUp, 
  Award,
  UserPlus,
  Target
} from 'lucide-react';

interface ReferralData {
  referral_code: string;
  total_referrals: number;
  active_referrals: number;
  total_earned: number;
  pending_rewards: number;
  tier_bonus: number;
  next_tier_requirement: number;
}

interface ReferralUser {
  id: number;
  wallet_address: string;
  nickname: string;
  join_date: string;
  status: 'active' | 'inactive';
  total_transactions: number;
  contributed_volume: number;
  tier: string;
}

interface ReferralReward {
  id: number;
  type: 'signup' | 'transaction' | 'tier_bonus' | 'volume_bonus';
  amount: number;
  token: string;
  from_user: string;
  timestamp: string;
  status: 'pending' | 'paid';
}

const ReferralsPage: React.FC = () => {
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const [referredUsers, setReferredUsers] = useState<ReferralUser[]>([]);
  const [rewardHistory, setRewardHistory] = useState<ReferralReward[]>([]);
  const [loading, setLoading] = useState(true);
  const [walletAddress, setWalletAddress] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    checkWalletConnection();
  }, []);

  const checkWalletConnection = () => {
    const savedAddress = localStorage.getItem('walletAddress');
    if (savedAddress) {
      setWalletAddress(savedAddress);
      fetchReferralData(savedAddress);
    } else {
      setLoading(false);
    }
  };

  const fetchReferralData = async (address: string) => {
    try {
      setLoading(true);
      
      // 추천 데이터 가져오기
      const referralResponse = await fetch(`http://localhost:3600/api/referrals/${address}`);
      if (referralResponse.ok) {
        const data = await referralResponse.json();
        if (data.success) {
          setReferralData(data.data);
        }
      }

      // 추천한 사용자 목록 가져오기
      const usersResponse = await fetch(`http://localhost:3600/api/referrals/${address}/users`);
      if (usersResponse.ok) {
        const data = await usersResponse.json();
        if (data.success) {
          setReferredUsers(data.data);
        }
      }

      // 보상 내역 가져오기
      const rewardsResponse = await fetch(`http://localhost:3600/api/referrals/${address}/rewards`);
      if (rewardsResponse.ok) {
        const data = await rewardsResponse.json();
        if (data.success) {
          setRewardHistory(data.data);
        }
      }
    } catch (error) {
      console.error('추천 데이터 가져오기 실패:', error);
      // 더미 데이터
      setReferralData({
        referral_code: 'VOLTRON123',
        total_referrals: 15,
        active_referrals: 12,
        total_earned: 1250.50,
        pending_rewards: 75.25,
        tier_bonus: 1.5,
        next_tier_requirement: 25
      });
      
      setReferredUsers([
        {
          id: 1,
          wallet_address: '0x1234...5678',
          nickname: 'EnergyTrader01',
          join_date: '2024-01-15T10:30:00Z',
          status: 'active',
          total_transactions: 45,
          contributed_volume: 12500,
          tier: 'Silver'
        },
        {
          id: 2,
          wallet_address: '0x2345...6789',
          nickname: 'VoltronUser02',
          join_date: '2024-01-18T14:20:00Z',
          status: 'active',
          total_transactions: 23,
          contributed_volume: 8750,
          tier: 'Bronze'
        }
      ]);
      
      setRewardHistory([
        {
          id: 1,
          type: 'signup',
          amount: 50,
          token: 'VTTN',
          from_user: 'EnergyTrader01',
          timestamp: '2024-01-15T10:35:00Z',
          status: 'paid'
        },
        {
          id: 2,
          type: 'transaction',
          amount: 25,
          token: 'VTTN',
          from_user: 'VoltronUser02',
          timestamp: '2024-01-18T15:10:00Z',
          status: 'pending'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralData) {
      navigator.clipboard.writeText(referralData.referral_code);
      alert('추천 코드가 클립보드에 복사되었습니다!');
    }
  };

  const shareReferralLink = () => {
    if (referralData) {
      const referralLink = `https://voltron.network/register?ref=${referralData.referral_code}`;
      navigator.clipboard.writeText(referralLink);
      alert('추천 링크가 클립보드에 복사되었습니다!');
    }
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
  };

  const getRewardTypeText = (type: string) => {
    switch (type) {
      case 'signup': return '가입 보상';
      case 'transaction': return '거래 보상';
      case 'tier_bonus': return '티어 보너스';
      case 'volume_bonus': return '볼륨 보너스';
      default: return type;
    }
  };

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'signup': return <UserPlus className="w-4 h-4 text-green-500" />;
      case 'transaction': return <TrendingUp className="w-4 h-4 text-blue-500" />;
      case 'tier_bonus': return <Award className="w-4 h-4 text-purple-500" />;
      case 'volume_bonus': return <Target className="w-4 h-4 text-orange-500" />;
      default: return <Gift className="w-4 h-4 text-gray-500" />;
    }
  };

  if (!walletAddress) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            <Users className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">추천 시스템</h2>
            <p className="text-gray-600 mb-6">
              지갑을 연결하여 추천 시스템을 이용하세요.
            </p>
            <button
              onClick={() => window.location.href = '/wallet'}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              지갑 연결하기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <Star className="w-8 h-8 text-orange-500" />
              <span>추천 시스템</span>
            </h1>
            <p className="text-gray-600 mt-1">
              친구를 초대하고 함께 보상을 받으세요!
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">데이터를 불러오는 중...</p>
        </div>
      ) : (
        <>
          {/* 추천 코드 및 통계 */}
          {referralData && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* 추천 코드 */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">내 추천 코드</h3>
                  <Share2 className="w-6 h-6 opacity-80" />
                </div>
                <div className="text-3xl font-bold mb-4 font-mono">
                  {referralData.referral_code}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={copyReferralCode}
                    className="flex-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1"
                  >
                    <Copy className="w-4 h-4" />
                    <span>복사</span>
                  </button>
                  <button
                    onClick={shareReferralLink}
                    className="flex-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>공유</span>
                  </button>
                </div>
              </div>

              {/* 추천 통계 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">추천 통계</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">총 추천 수</span>
                    <span className="font-semibold text-gray-900">{referralData.total_referrals}명</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">활성 추천 수</span>
                    <span className="font-semibold text-green-600">{referralData.active_referrals}명</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">티어 보너스</span>
                    <span className="font-semibold text-purple-600">{referralData.tier_bonus}x</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="text-sm text-gray-600 mb-1">
                      다음 티어까지: {referralData.next_tier_requirement - referralData.total_referrals}명
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${Math.min((referralData.total_referrals / referralData.next_tier_requirement) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 수익 통계 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">수익 통계</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">총 수익</span>
                    <span className="font-semibold text-green-600">
                      {formatAmount(referralData.total_earned)} VTTN
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">대기 중 보상</span>
                    <span className="font-semibold text-orange-600">
                      {formatAmount(referralData.pending_rewards)} VTTN
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">예상 월 수익</span>
                    <span className="font-semibold text-blue-600">
                      {formatAmount(referralData.total_earned * 0.1)} VTTN
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="text-sm text-gray-600">USD 환산</div>
                    <div className="text-lg font-bold text-gray-900">
                      ${(referralData.total_earned * 0.125).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 탭 메뉴 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {[
                  { id: 'overview', name: '개요', icon: Star },
                  { id: 'users', name: '추천한 사용자', icon: Users },
                  { id: 'rewards', name: '보상 내역', icon: Gift }
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-orange-500 text-orange-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.name}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">추천 시스템 안내</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-900">보상 구조</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• 가입 시: 50 VTTN</li>
                          <li>• 첫 거래 시: 25 VTTN</li>
                          <li>• 거래량 보너스: 0.1%</li>
                          <li>• 티어 보너스: 최대 3x</li>
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-900">티어 시스템</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• Bronze: 1-9명 (1x)</li>
                          <li>• Silver: 10-24명 (1.5x)</li>
                          <li>• Gold: 25-49명 (2x)</li>
                          <li>• Diamond: 50명+ (3x)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'users' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">추천한 사용자</h3>
                  <div className="space-y-3">
                    {referredUsers.length > 0 ? (
                      referredUsers.map((user) => (
                        <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${
                              user.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                            }`}></div>
                            <div>
                              <div className="font-medium text-gray-900">{user.nickname}</div>
                              <div className="text-sm text-gray-600">
                                {formatAddress(user.wallet_address)} • {user.tier} 티어
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-600">거래량</div>
                            <div className="font-medium text-gray-900">
                              ${user.contributed_volume.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        아직 추천한 사용자가 없습니다.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'rewards' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">보상 내역</h3>
                  <div className="space-y-3">
                    {rewardHistory.length > 0 ? (
                      rewardHistory.map((reward) => (
                        <div key={reward.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            {getRewardIcon(reward.type)}
                            <div>
                              <div className="font-medium text-gray-900">
                                {getRewardTypeText(reward.type)}
                              </div>
                              <div className="text-sm text-gray-600">
                                {reward.from_user} • {new Date(reward.timestamp).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-green-600">
                              +{formatAmount(reward.amount)} {reward.token}
                            </div>
                            <div className={`text-xs px-2 py-1 rounded-full ${
                              reward.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {reward.status === 'paid' ? '지급완료' : '대기중'}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        보상 내역이 없습니다.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReferralsPage;